import React, { useCallback, type Ref, type MouseEvent, useMemo } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { asyncInlineEditStatusModal } from '@atlassian/jira-inline-edit-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { Trigger, useTriggerContext } from '@atlassian/entry-point-panel';
import {
	ISSUE_VIEW_SOURCE,
	type ConfigureSource,
	type Status,
} from '@atlassian/jira-status-config-common/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { editStatusExperience } from '../../../experiences.tsx';
import { ConfigureStatusPanel } from './ConfigureStatusPanel.tsx';
import { messages } from './messages.tsx';

type OnStatusCreatedOrUpdatedOptions = { shouldRefreshCurrentStatus?: boolean };

export type EditStatusOptionProps = {
	projectId: string;
	projectKey: string;
	issueTypeId: string;
	statusNames?: string[];
	status: Status;
	statuses: Status[];
	onClick: () => void;
	onClose: () => void;
	onStatusUpdated?: (options?: OnStatusCreatedOrUpdatedOptions) => Promise<void>;
	buttonRef: Ref<HTMLButtonElement>;
	source?: ConfigureSource;
	shouldTriggerModal?: boolean;
};

export const EditStatusOption = ({
	projectId,
	projectKey,
	issueTypeId,
	statusNames = [],
	status,
	statuses,
	onClick,
	onClose,
	onStatusUpdated = Promise.resolve,
	buttonRef,
	source,
	shouldTriggerModal = true,
}: EditStatusOptionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const trigger = useTriggerContext(ConfigureStatusPanel);

	const handleClick = useCallback(
		(_e: MouseEvent<HTMLElement>) => {
			onClick();
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'editStatusButton');
			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleChange
			// will be called which will trigger click manually again and cause double modal rendering
			_e.stopPropagation();
		},
		[onClick, createAnalyticsEvent],
	);

	const onModalClose = useCallback(() => {
		onClose();
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineEditStatusModal',
			}),
		);
	}, [createAnalyticsEvent, onClose]);

	const onMouseDown = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editStatusButton',
		);
	}, [createAnalyticsEvent]);

	const entryPointProps = useMemo(
		() => ({
			status,
			statuses,
			statusNames,
			projectId,
			projectKey,
			onClose: onModalClose,
			isNewControllerEnabled: true,
			issueTypeId,
			editStatusExperience,
			source: source || ISSUE_VIEW_SOURCE,
			onStatusUpdated,
			shouldShowStatusSelect: true,
			isWrappedInModal: shouldTriggerModal,
		}),
		[
			statusNames,
			projectId,
			projectKey,
			issueTypeId,
			source,
			status,
			statuses,
			onStatusUpdated,
			onModalClose,
			shouldTriggerModal,
		],
	);

	const getClickElement = useCallback(
		(ref: React.RefObject<HTMLElement>) => (
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				role="button"
				xcss={optionStyles}
				onClick={handleClick}
				data-show-on-hover
				ref={ref}
				onMouseDown={(event: MouseEvent<HTMLElement>) => {
					if (event.button === 0) {
						// Left mouse button was clicked
						onMouseDown();
					}
				}}
			>
				{formatMessage(messages.editStatus)}
			</Box>
		),
		[handleClick, formatMessage, onMouseDown],
	);

	const modalTrigger = fg('inline_config_migrate_deprecated_modal_entrypoints') ? (
		<ModalTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			modalProps={defaultModalProps}
			forwardedRef={buttonRef}
			Fallback={() => <FormDialogSkeleton numFormFields={3} />}
		>
			{({ ref }) => getClickElement(ref)}
		</ModalTrigger>
	) : (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			interactionName="edit-status-modal"
			modalProps={defaultModalProps}
			useInternalModal
			forwardedRef={buttonRef}
			Fallback={
				fg('jgfeng_new_work_type_fallback_loading_state')
					? () => <FormDialogSkeleton numFormFields={3} />
					: undefined
			}
		>
			{({ ref }) => getClickElement(ref)}
		</ModalEntryPointPressableTrigger>
	);

	return shouldTriggerModal ? (
		modalTrigger
	) : (
		<Trigger
			trigger={trigger}
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			fallback={<FormDialogSkeleton numFormFields={3} />}
		>
			{({ ref }) => getClickElement(ref)}
		</Trigger>
	);
};

const optionStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.medium',
});
